import React, { Fragment, useContext, useEffect, useState } from "react";
import { IContext, RequestStatus, Walkaround, WalkaroundCheckList, api } from "../../../logic/api";
import flex from "../../../styles/flex";
import { Button, IconButton } from "@mui/material";
import { FiArrowDownCircle, FiArrowUpCircle, FiCheck, FiCopy, FiDownload, FiEdit, FiSave, FiTrash2 } from "react-icons/fi";
import { CopyToClipboard } from 'react-copy-to-clipboard';
import NotchedCard from "../../../components/cards/NotchedCard/NotchedCard";
import { FaCheckSquare, FaSquare, FaTimes, FaUndo } from "react-icons/fa";
import { ISessionState, SessionContext } from "../../../utility/useSession";
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import EditableField, { EditableFieldTypes } from "../../../components/editable/EditableField/EditableField";
import EditableGroup, { EditableGroupType } from "../../../components/editable/EditableGroup/EditableGroup";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import ConfirmationDialog from "../../../components/notifications/confirmationDialog/ConfirmationDialog";
import PhotoPickerCompact from "../../../components/upsells/PhotoPicker/PhotoPickerCompact";
import { useTranslation } from "react-i18next";

export interface IWalkaroundCheckListListItemProps {
    item: WalkaroundCheckList;
    index: number;
    isLast: boolean;
    editing: boolean;
    hasSelection: boolean;
    walkarounds: Walkaround[];
    onOrdinalUpdated: () => void;
    onDelete: () => void;
    onRestore: () => void;
    onEdit: () => void;
    onCancel: () => void;
    onSave: (wal: WalkaroundCheckList, manageSubItems: boolean) => void;

}

export const WaCheckListItem: React.FC<IWalkaroundCheckListListItemProps> = (props) => {
    const session = useContext(SessionContext);
    const { t } = useTranslation();
    const moveUp = api.hooks.walkaroundCheckList.moveUp();
    const moveDown = api.hooks.walkaroundCheckList.moveDown();
    const save = api.hooks.walkaroundCheckList.save();
    const [state, setState] = useState<WalkaroundCheckList | null>(props.item == null ? null : { ...props.item });
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    useEffect(() => {
        setState(props.item == null ? null : { ...props.item });
    }, [props.item]);


    const handleMoveUp = () => {
        if (props.item.id == null) return;
        moveUp.send(session.current.context, {
            id: props.item.id,
        }, (r) => {
            if (r === true) {
                props.onOrdinalUpdated();
            }
        });
    };

    const handleMoveDown = () => {
        if (props.item.id == null) return;
        moveDown.send(session.current.context, {
            id: props.item.id,
        }, (r) => {
            if (r === true) {
                props.onOrdinalUpdated();
            }
        });
    };

    const handleQRPreviewDownload = (elementId: string, ext: string) => {
        const element = document.getElementById(elementId);

        if (ext === 'svg' && element instanceof SVGSVGElement) {
            // Get the SVG data as a string
            const svgData = new XMLSerializer().serializeToString(element);

            // Convert SVG string to Blob
            const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });

            // Create a URL for the Blob
            const svgUrl = URL.createObjectURL(svgBlob);

            // Proceed with download
            const downloadLink = document.createElement("a");
            downloadLink.href = svgUrl;
            downloadLink.download = `${props.item.qrCode}.${ext}`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);

            // Clean up by revoking the Object URL
            URL.revokeObjectURL(svgUrl);
        } else {
            const contentType = ext === 'png' ? 'image/png' : 'image/jpeg';
            const canvas = element instanceof HTMLCanvasElement ? element : null;
            if (canvas != null) {
                canvas.toBlob((blob) => {
                    if (blob != null) {
                        const url = URL.createObjectURL(blob);
                        const downloadLink = document.createElement("a");
                        downloadLink.href = url;
                        downloadLink.download = `${props.item.qrCode}.${ext}`;
                        document.body.appendChild(downloadLink);
                        downloadLink.click();
                        document.body.removeChild(downloadLink);
                        URL.revokeObjectURL(url);
                    }
                }, contentType);
            }

        }
    };

    const handleDeleteIconPressed = () => {
        setShowDeleteConfirmation(true);
    };

    const handleDeleteConfirmationResult = (result: boolean) => {
        setShowDeleteConfirmation(false);
        if (result) {
            props.onDelete();
        }
    }

    const updateWalkaround = async (wa: WalkaroundCheckList) => {
        setState(wa);
        return true;
    };

    const handleSave = () => {
        if (state == null) return;
        save.send(session.current.context, state, (r) => {
            if (r == null) return;
            props.onSave(r, false);
        });
    }

    return (
        <Fragment>
            <div style={{ ...flex.flexRows, justifyContent: "center", alignItems: "center" }}>
                {(props.editing == true && props.item.active == true && props.item.id != null) && (
                    <div style={{ width: 70, paddingRight: 20, marginBottom: 40 }}>
                        <div style={flex.flexColumns}>
                            <IconButton disabled={props.index === 0} onClick={handleMoveUp}>
                                <FiArrowUpCircle size={40}></FiArrowUpCircle>
                            </IconButton>
                            <IconButton disabled={props.isLast} onClick={handleMoveDown}>
                                <FiArrowDownCircle size={40}></FiArrowDownCircle>
                            </IconButton>
                        </div>
                    </div>)}
                <div style={{ ...flex.flexFill, opacity: props.item.active ? 1 : 0.8 }}>
                    {props.item.parentId == null && (
                        topLevelCheckListItemCard(props, state, updateWalkaround, setState, handleQRPreviewDownload, save, session)
                    )}
                    {props.item.parentId != null && (
                        subCheckListItemCard(props, state, updateWalkaround, setState)
                    )}
                </div>
                <div style={{ ...flex.flexColumns, width: 65 }}>
                    {props.item.active && props.editing && (
                        <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
                            <div>
                                <IconButton onClick={handleDeleteIconPressed}>
                                    <FiTrash2 size={40}></FiTrash2>
                                </IconButton>
                            </div>
                        </div>
                    )}
                    {!props.editing && !props.hasSelection && (
                        <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
                            <div>
                                <IconButton onClick={props.onEdit}>
                                    <FiEdit color="rgb(235, 48, 79)" size={40}></FiEdit>
                                </IconButton>
                            </div>
                        </div>
                    )}
                    {(props.editing && state != null) && (
                        <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
                            <div>
                                <IconButton onClick={() => handleSave()}>
                                    <FiSave color="green" size={40}></FiSave>
                                </IconButton>
                            </div>
                        </div>
                    )}
                    {props.editing && (
                        <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
                            <div>
                                <IconButton onClick={props.onCancel}>
                                    <FaTimes color="rgb(235, 48, 79)" size={40}></FaTimes>
                                </IconButton>
                            </div>
                        </div>
                    )}
                    {props.item.active === false && props.editing && (
                        <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
                            <div>
                                <IconButton onClick={props.onRestore}>
                                    <FaUndo size={40}></FaUndo>
                                </IconButton>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <ProgressDialog status={save.status} />
            <ErrorDialog err={save.err} />
            <ProgressDialog status={moveUp.status} />
            <ErrorDialog err={moveUp.err} />
            <ProgressDialog status={moveDown.status} />
            <ErrorDialog err={moveDown.err} />
            <ConfirmationDialog title={t("walkarounds.delete", { item: props.item.parentId == null ? t("walkarounds.checklist") : t("walkarounds.check") })} message={t("walkarounds.deleteConfirmationMessage", { item: props.item.parentId == null ? t("walkarounds.checklist") : t("walkarounds.check") })} show={showDeleteConfirmation} onClose={handleDeleteConfirmationResult} />
        </Fragment>
    );
};


function topLevelCheckListItemCard(props: React.PropsWithChildren<IWalkaroundCheckListListItemProps>, state: WalkaroundCheckList | null, updateWalkaround: (wa: WalkaroundCheckList) => Promise<boolean>, setState: React.Dispatch<React.SetStateAction<WalkaroundCheckList | null>>, handleQRPreviewDownload: (elementId: string, ext: string) => void,
    save: {
        send: (context: IContext,
            parameters: WalkaroundCheckList, callBack?: ((value: WalkaroundCheckList | null) => void) |
                undefined) => void; data: WalkaroundCheckList | null; status: RequestStatus; err: unknown; params: WalkaroundCheckList | null; callBack: (value: WalkaroundCheckList | null) => void; context: IContext | null;
    }, session: ISessionState): React.ReactNode {

    const [showCopiedIcon, setShowCopiedIcon] = useState<boolean>(false);
    const { t } = useTranslation();

    return <NotchedCard borderColor={props.editing ? "rgb(235, 48, 79)" : null} label={t('walkarounds.checklistNotchedCard', {
        new: props.item.id == null ? t("walkarounds.new") : "",
        count: props.index + 1,
        deleted: props.item.active ? "" : t("walkarounds.deletedWithBrackets"),
    })}>
        <div style={{ ...flex.flexRows }}>
            {(props.editing == false) && (
                <Fragment>
                    <div style={{ ...flex.flexAuto, width: 25 }}> </div>
                    <div style={flex.flexFill}>
                        <div style={{ height: 20 }}></div>
                        <div><b style={{ fontSize: 20, }}>{props.item.config.name ?? ""}</b></div>
                        <div style={{ height: 8 }}></div>
                        <div>{props.item.config.direction ?? ""}</div>
                    </div>
                </Fragment>
            )}
            {(props.editing == true && state != null) && (
                <Fragment>
                    <div style={{ ...flex.flexAuto, width: 350 }}>
                        <EditableGroup<WalkaroundCheckList>
                            id="wa_checklists_inline"
                            testId="walkaround-checklist-name-group"
                            editType={EditableGroupType.alwaysEditing}
                            data={state}
                            onDataChanged={(data) => updateWalkaround(data)}
                        >
                            <EditableField<WalkaroundCheckList, string>
                                inputType={EditableFieldTypes.text}
                                label={t("input.labels.name")}
                                required={true}
                                columnWidth={12}
                                maxLength={30}
                                maxWidth={350}
                                showCharacterCount={true}
                                useFullWidthWhenEditing={true}
                                onProvide={(data) => data.config.name ?? ""}
                                onChange={(data, value) => data.config.name = value} />
                            <EditableField<WalkaroundCheckList, string>
                                inputType={EditableFieldTypes.textMultiLine}
                                label={t("walkarounds.qrDirections")}
                                useFullWidthWhenEditing={true}
                                maxLength={80}
                                maxWidth={350}
                                columnWidth={12}
                                showCharacterCount={true}
                                required={true}
                                onProvide={(data) => (data.config.direction ?? "")}
                                onChange={(data, value) => data.config.direction = (value ?? "")} />
                        </EditableGroup>
                    </div>
                    <div style={flex.flexFill}></div>
                </Fragment>
            )}
            <div style={flex.flexAuto}>
                <div style={{ fontSize: 18 }}>{t("walkarounds.qrImage")}</div>
                <div style={{ marginTop: 5, width: 150, }}>
                    <PhotoPickerCompact
                        ignoreSizeConstraints={true}

                        readonly={props.editing == false}
                        scale={1}
                        width={150}
                        height={150}
                        fileName={state?.config.photoFilename ?? ""}
                        onUpdated={(fileName) => {
                            if (state == null) return;
                            state.config.photoFilename = fileName;
                            setState({ ...state });
                        }}
                    ></PhotoPickerCompact>
                </div>
            </div>
            <div style={{ ...flex.flexAuto, width: 40 }}> </div>
            <div style={flex.flexAuto}>
                <div style={{ fontSize: 18 }}>{t("walkarounds.qrPreview")}</div>
                <div style={{ marginTop: 5 }}>
                    <QRCodeSVG id={`${props.item.qrCode}_SVG`} value={props.item.qrCode} size={150} />
                    <QRCodeCanvas id={`${props.item.qrCode}_CNV`} style={{ display: "none" }} value={props.item.qrCode} size={150} />
                </div>
                <div style={{ height: 10 }}></div>
                <div style={flex.flexRows}>
                    <div style={{ ...flex.flexAuto, textAlign: "left", height: 35 }}>
                        <a
                            onClick={() => handleQRPreviewDownload(`${props.item.qrCode}_SVG`, "svg")}
                            style={{ "cursor": "pointer" }}>
                            <div style={flex.flexRows}>
                                <FiDownload size={18}></FiDownload>
                                <div style={{ width: 4 }}></div>
                                <div><small style={{ fontSize: 14 }}>SVG</small></div>
                            </div>

                        </a>
                    </div>
                    <div style={{ width: 10 }}></div>
                    <div style={{ ...flex.flexAuto, textAlign: "left", height: 35 }}>
                        <a
                            onClick={() => handleQRPreviewDownload(`${props.item.qrCode}_CNV`, "png")}
                            style={{ "cursor": "pointer" }}>
                            <div style={flex.flexRows}>
                                <FiDownload size={18}></FiDownload>
                                <div style={{ width: 4 }}></div>
                                <div><small style={{ fontSize: 14 }}>PNG</small></div>
                            </div>
                        </a>
                    </div>
                    <div style={{ width: 10 }}></div>
                    <div style={{ ...flex.flexAuto, textAlign: "left", height: 35 }}>
                        <a
                            onClick={() => handleQRPreviewDownload(`${props.item.qrCode}_CNV`, "jpg")}
                            style={{ "cursor": "pointer" }}>
                            <div style={flex.flexRows}>
                                <FiDownload size={18}></FiDownload>
                                <div style={{ width: 4 }}></div>
                                <div><small style={{ fontSize: 14 }}>JPG</small></div>
                            </div>
                        </a>
                    </div>
                </div>
            </div>
            <div style={{ ...flex.flexAuto, width: 20 }}> </div>
            <div style={{ ...flex.flexAuto }}>
                <div style={{ fontSize: 18 }}>{t("walkarounds.qrString")}</div>
                <div style={{ ...flex.flexAuto, height: 20 }}> </div>
                <div style={{ ...flex.flexRows }}>
                    <div style={{ ...flex.flexFill }}>
                        <div style={{ fontSize: 20, fontWeight: "bold" }}>
                            {props.item.qrCode}
                        </div>
                    </div>
                    <div style={{ ...flex.flexAuto, width: 8 }}> </div>
                    <div style={{ ...flex.flexAuto, textAlign: "left", cursor: "pointer" }}>

                        <CopyToClipboard text={props.item.qrCode} onCopy={() => {
                            setShowCopiedIcon(true);
                            setTimeout(() => {
                                setShowCopiedIcon(false);
                            }, 1000);
                        }}>
                            <div style={{ padding: 3 }}>
                                {!showCopiedIcon && (<FiCopy size={20}></FiCopy>)}
                                {showCopiedIcon && (<FiCheck color="green" size={20}></FiCheck>)}

                            </div>
                        </CopyToClipboard>
                    </div>
                </div>
                <div style={flex.flexRows}>
                    <div style={{ ...flex.flexFill }}>
                        <div style={{ width: 230, height: 162 }}>

                            <Button
                                style={{ marginTop: 40, fontSize: 16, fontWeight: "normal", backgroundColor: "black", color: "white", paddingTop: 16, paddingBottom: 16, paddingLeft: 20, paddingRight: 20 }}
                                variant="text"
                                onClick={() => {
                                    if (!state) return;
                                    if (!props.editing) {
                                        props.onSave(state, true);
                                        return;
                                    }
                                    save.send(session.current.context, state, (r) => {
                                        if (r == null) return;
                                        props.onSave(r, true);
                                    });
                                }}
                            >
                                {t("walkarounds.manageChecklist")}
                            </Button>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    </NotchedCard>;
}

function subCheckListItemCard(props: React.PropsWithChildren<IWalkaroundCheckListListItemProps>, state: WalkaroundCheckList | null, updateWalkaround: (wa: WalkaroundCheckList) => Promise<boolean>, setState: React.Dispatch<React.SetStateAction<WalkaroundCheckList | null>>): React.ReactNode {

    const { t } = useTranslation();

    return <NotchedCard borderColor={props.editing ? "rgb(235, 48, 79)" : null} label={t('walkarounds.checkNotchedCard', {
        new: props.item.id == null ? t("general.new") : "",
        count: props.index + 1,
        deleted: props.item.active ? "" : t("walkarounds.deletedWithBrackets"),
    })}>
        <div style={{ ...flex.flexRows }}>
            {(props.editing == false) && (
                <Fragment>
                    <div style={{ ...flex.flexAuto, width: 25 }}> </div>
                    <div style={flex.flexFill}>
                        <div style={{ height: 20 }}></div>
                        <small>{t("walkarounds.whatToCheck")}:</small>
                        <div><b style={{ fontSize: 16, }}>{props.item.config.whatToCheck ?? ""}</b></div>
                        <div style={{ height: 8 }}></div>
                        <small>{t("walkarounds.whatToCheckFor")}:</small>
                        <div><b style={{ fontSize: 16, }}>{props.item.config.whatToCheckFor ?? ""}</b></div>
                    </div>
                </Fragment>
            )}
            {(props.editing == false) && (
                <Fragment>
                    <div style={{ ...flex.flexAuto, width: 25 }}> </div>
                    <div style={flex.flexFill}>
                        <div style={{ height: 20 }}></div>
                        <small>{t("walkarounds.howToCheck")}:</small>
                        <div><b style={{ fontSize: 16, }}>{props.item.config.howToCheck ?? ""}</b></div>
                        <div style={{ height: 20 }}></div>
                        <small>{t("walkarounds.photoRequired")}</small>
                        <div><b style={{ fontSize: 16, }}>{props.item.config.photoRequired == true ? t("walkarounds.photoRequiredYes") : t("walkarounds.photoRequiredNo")}</b></div>

                    </div>
                </Fragment>
            )}
            {(props.editing == true && state != null) && (
                <Fragment>
                    <div style={{ ...flex.flexFill }}>
                        <EditableGroup<WalkaroundCheckList>
                            id="wa_checklistitems_inline"
                            testId="walkaround-checklist-what-group"
                            editType={EditableGroupType.alwaysEditing}
                            data={state}
                            onDataChanged={(data) => updateWalkaround(data)}
                        >
                            <EditableField<WalkaroundCheckList, string>
                                inputType={EditableFieldTypes.text}
                                label={t("walkarounds.whatToCheck")}
                                required={true}
                                columnWidth={12}
                                maxLength={60}
                                maxWidth={300}
                                showCharacterCount={true}
                                useFullWidthWhenEditing={true}
                                onProvide={(data) => data.config.whatToCheck ?? ""}
                                onChange={(data, value) => data.config.whatToCheck = value} />
                            <EditableField<WalkaroundCheckList, string>
                                inputType={EditableFieldTypes.textMultiLine}
                                label={t("walkarounds.whatToCheckFor")}
                                useFullWidthWhenEditing={true}
                                maxLength={60}
                                maxWidth={300}
                                columnWidth={12}
                                showCharacterCount={true}
                                required={true}
                                onProvide={(data) => (data.config.whatToCheckFor ?? "")}
                                onChange={(data, value) => data.config.whatToCheckFor = (value ?? "")} />


                        </EditableGroup>

                    </div>
                    <div style={{ ...flex.flexFill }}>
                        <EditableGroup<WalkaroundCheckList>
                            id="wa_checklistitems_inline"
                            testId="walkaround-checklist-how-group"
                            editType={EditableGroupType.alwaysEditing}
                            data={state}
                            onDataChanged={(data) => updateWalkaround(data)}
                        >

                            <EditableField<WalkaroundCheckList, string>
                                inputType={EditableFieldTypes.textMultiLine}
                                label={t("walkarounds.howToCheck")}
                                useFullWidthWhenEditing={true}
                                maxLength={200}
                                maxWidth={300}
                                columnWidth={12}
                                showCharacterCount={true}
                                required={true}
                                onProvide={(data) => (data.config.howToCheck ?? "")}
                                onChange={(data, value) => data.config.howToCheck = (value ?? "")} />

                            <EditableField<WalkaroundCheckList, boolean>
                                inputType={EditableFieldTypes.switch}
                                alwaysVisible={true}

                                label={t("walkarounds.photoRequired")}
                                onProvide={(data) => data.config.photoRequired ?? false}
                                onChange={(data, value) => data.config.photoRequired = value}
                            />
                        </EditableGroup>
                    </div>
                </Fragment>
            )}
            <div style={flex.flexAuto}>
                <div style={{ fontSize: 18 }}>{t("walkarounds.image")}</div>
                <div style={{ marginTop: 5, width: 150, }}>
                    <PhotoPickerCompact
                        ignoreSizeConstraints={true}
                        readonly={props.editing == false}
                        scale={1}
                        width={150}
                        height={150}
                        fileName={state?.config.photoFilename ?? ""}
                        onUpdated={(fileName) => {
                            if (state == null) return;
                            state.config.photoFilename = fileName;
                            setState({ ...state });
                        }}
                    ></PhotoPickerCompact>
                </div>
            </div>

            <div style={{ ...flex.flexAuto, width: 20 }}> </div>
            <div style={flex.flexFill}>
                <div style={{ fontSize: 18 }}>{t("walkarounds.title")}</div>
                <div style={{ height: 5 }}></div>
                {props.walkarounds.map((wa, index) => {
                    const checked = state?.config.walkaroundIds?.includes(wa.id ?? -1) ?? false;
                    if (props.editing == false && checked == false) {
                        return null;
                    }
                    return <div onClick={() => {
                        if (props.editing == false) return;
                        if (state == null) return;
                        if (state.config.walkaroundIds == null) state.config.walkaroundIds = [];
                        if (checked) {
                            state.config.walkaroundIds = state.config.walkaroundIds.filter((id) => id != wa.id);
                        } else {
                            state.config.walkaroundIds.push(wa.id ?? -1);
                        }
                        setState({ ...state });
                    }} key={index} style={{ display: 'inline-block', width: 100, padding: 8, cursor: props.editing ? 'pointer' : 'default' }}>
                        <div style={flex.flexRows}>
                            {(checked) && (
                                <FaCheckSquare color={props.editing ? "black" : "#aaa"} size={20}></FaCheckSquare>
                            )}
                            {(!checked) && (
                                <FaSquare size={20}></FaSquare>
                            )}
                            <div style={{ width: 8 }}></div>
                            {wa.abbrev}
                        </div>
                    </div>;
                }
                )}
            </div>
        </div>
    </NotchedCard>;
}
